import { useState, useEffect } from "react";
import {
  Key, Copy, Plus, Trash2, ToggleLeft, ToggleRight,
  ChevronDown, ChevronRight, Code, Phone, Globe,
  FileText, Shield, Zap, CheckCircle2, AlertTriangle, ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API, apiCall } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const CodeBlock = ({ title, code, lang = "bash" }) => {
  const copy = () => { navigator.clipboard.writeText(code); toast.success("Copiado"); };
  return (
    <div className="rounded-lg overflow-hidden border border-zinc-800 my-3">
      <div className="flex items-center justify-between bg-zinc-900 px-4 py-2 border-b border-zinc-800">
        <span className="text-xs text-zinc-400 font-mono">{title || lang}</span>
        <button onClick={copy} className="text-zinc-500 hover:text-white transition"><Copy className="w-3.5 h-3.5" /></button>
      </div>
      <pre className="p-4 bg-zinc-950 text-sm text-zinc-300 overflow-x-auto font-mono leading-relaxed"><code>{code}</code></pre>
    </div>
  );
};

const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden mb-4">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 bg-zinc-900/50 hover:bg-zinc-900 transition text-left">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-violet-400" />}
          <span className="text-white font-semibold">{title}</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && <div className="p-5 border-t border-zinc-800 bg-zinc-950/50">{children}</div>}
    </div>
  );
};

const ApiKeysManager = () => {
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [newLimit, setNewLimit] = useState("");
  const [creating, setCreating] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    const { ok, data } = await apiCall("GET", `${API}/keys`);
    if (ok) setKeys(data.keys || []);
  };

  const createKey = async () => {
    if (!newName.trim()) return toast.error("Nombre requerido");
    setCreating(true);
    try {
      const { ok, data } = await apiCall("POST", `${API}/keys`, { company_name: newName.trim(), limit_per_month: newLimit ? parseInt(newLimit) : null });
      if (!ok) throw new Error(data.detail || "Error");
      toast.success("API Key creada");
      setNewName(""); setNewLimit("");
      fetchKeys();
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  const toggleKey = async (keyId, active) => {
    await apiCall("PATCH", `${API}/keys/${keyId}`, { is_active: active });
    fetchKeys();
  };

  const deleteKey = async () => {
    if (!keyToDelete) return;
    await apiCall("DELETE", `${API}/keys/${keyToDelete}`);
    toast.success("Key eliminada");
    setKeyToDelete(null);
    fetchKeys();
  };

  const copyKey = (key) => { navigator.clipboard.writeText(key); toast.success("API Key copiada"); };

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Nombre de la empresa" className="bg-zinc-950 border-zinc-800 text-white flex-1" data-testid="api-key-name" />
        <Input value={newLimit} onChange={e => setNewLimit(e.target.value)} placeholder="Limite mensual (vacio=ilimitado)" type="number" className="bg-zinc-950 border-zinc-800 text-white w-64" data-testid="api-key-limit" />
        <Button onClick={createKey} disabled={creating} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="create-api-key-btn">
          <Plus className="w-4 h-4 mr-2" />Crear Key
        </Button>
      </div>
      <div className="space-y-2">
        {keys.map(k => (
          <div key={k.key_id} className="flex items-center justify-between p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg" data-testid={`api-key-${k.key_id}`}>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-white font-medium">{k.company_name}</span>
                <Badge className={k.is_active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-zinc-700 text-zinc-400"}>
                  {k.is_active ? "Activa" : "Inactiva"}
                </Badge>
                {k.limit_per_month && <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">Limite: {k.limit_per_month}/mes</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <code className="text-xs text-zinc-500 font-mono bg-zinc-800 px-2 py-1 rounded">{k.key}</code>
                <button onClick={() => copyKey(k.key)} className="text-zinc-500 hover:text-white"><Copy className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                <span>Este mes: {k.usage_this_month || 0}</span>
                <span>Total: {k.usage_total || 0}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => toggleKey(k.key_id, !k.is_active)} className="text-zinc-400 hover:text-white">
                {k.is_active ? <ToggleRight className="w-5 h-5 text-emerald-400" /> : <ToggleLeft className="w-5 h-5" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setKeyToDelete(k.key_id)} className="text-zinc-500 hover:text-rose-400">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
        {keys.length === 0 && <p className="text-zinc-600 text-center py-4">No hay API keys. Crea una para empezar.</p>}
      </div>
      <AlertDialog open={!!keyToDelete} onOpenChange={() => setKeyToDelete(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar API Key?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Esta accion es irreversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-white">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteKey} className="bg-rose-600 hover:bg-rose-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const BASE_URL = "https://veritel.com.ar/api";

export default function ApiDocsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />Volver
          </Button>
        </div>

        <div className="mb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2" data-testid="api-docs-title">API de Veritel</h1>
              <p className="text-zinc-400">Integra la verificacion de telefonos directamente en tu sistema.</p>
            </div>
            <Button
              onClick={async () => {
                const toastId = toast.loading("Generando PDF...");
                try {
                  const resp = await fetch(`${API}/keys/docs/pdf`, { credentials: "include" });
                  if (!resp.ok) throw new Error("Error al generar PDF");
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a"); a.href = url; a.download = "Veritel_API_Documentation.pdf"; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("PDF descargado", { id: toastId });
                } catch (e) { toast.error(e.message, { id: toastId }); }
              }}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="download-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />Descargar PDF
            </Button>
          </div>
        </div>

        <Section title="Tus API Keys" icon={Key} defaultOpen={true}>
          <ApiKeysManager />
        </Section>

        <Section title="Autenticacion" icon={Shield} defaultOpen={true}>
          <p className="text-zinc-300 text-sm mb-3">Todas las llamadas requieren el header <code className="bg-zinc-800 px-1.5 py-0.5 rounded text-violet-400">X-API-Key</code>.</p>
          <CodeBlock title="Header" code={`X-API-Key: sk_cv_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`} />
          <div className="flex items-start gap-2 mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-300">Nunca expongas tu API Key en codigo del frontend. Usala solo desde tu backend.</p>
          </div>
        </Section>

        <Section title="POST /external/verify/phone - Verificar un telefono" icon={Phone}>
          <p className="text-zinc-300 text-sm mb-3">Verifica un numero de telefono argentino. Opcionalmente consulta si tiene WhatsApp.</p>
          <CodeBlock title="Request" code={`curl -X POST ${BASE_URL}/external/verify/phone \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: tu_api_key" \\
  -d '{
    "phone": "3816668820",
    "check_whatsapp": true
  }'`} />
          <h4 className="text-sm font-semibold text-zinc-300 mt-4 mb-2">Parametros</h4>
          <div className="space-y-1 text-sm">
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><code className="text-violet-400 w-36">phone</code><span className="text-zinc-300">string (requerido) - Numero de telefono</span></div>
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><code className="text-violet-400 w-36">check_whatsapp</code><span className="text-zinc-300">boolean (opcional) - Si es true, consulta WhatsApp</span></div>
          </div>
          <CodeBlock title="Response" code={`{
  "original": "3816668820",
  "numero_normalizado": "381 6668820",
  "tipo": "CELULAR",
  "provincia": "Tucuman",
  "localidad": "SAN MIGUEL DE TUCUMAN",
  "operador": "TELECOM ARGENTINA S.A.",
  "is_valid": true,
  "whatsapp": "PENDIENTE"
}`} />
        </Section>

        <Section title="POST /external/verify/bulk - Verificacion masiva" icon={Zap}>
          <p className="text-zinc-300 text-sm mb-3">Verifica hasta 500 numeros en una sola llamada (sincrono). Incluye <code className="text-emerald-400">check_whatsapp: true</code> para verificar WhatsApp en los celulares.</p>
          <CodeBlock title="Request" code={`curl -X POST ${BASE_URL}/external/verify/bulk \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: tu_api_key" \\
  -d '{
    "phones": ["3816668820", "1155551234", "2614551234"],
    "check_whatsapp": true
  }'`} />
          <CodeBlock title="Response" code={`{
  "total": 3,
  "summary": { "celular": 2, "fijo": 0, "invalido": 1 },
  "whatsapp_summary": { "si": 1, "no": 1, "error": 0 },
  "results": [
    {
      "original": "3816668820",
      "numero_normalizado": "381 6668820",
      "tipo": "CELULAR",
      "provincia": "Tucuman",
      "localidad": "SAN MIGUEL DE TUCUMAN",
      "is_valid": true,
      "tiene_whatsapp": "SI"
    }
  ]
}`} />
        </Section>

        <Section title="GET /external/campaigns - Listar campanas" icon={FileText}>
          <p className="text-zinc-300 text-sm mb-3">Obtiene la lista de campanas de tu empresa con su estado y resumen.</p>
          <CodeBlock title="Request" code={`curl -X GET ${BASE_URL}/external/campaigns \\
  -H "X-API-Key: tu_api_key"`} />
          <CodeBlock title="Response" code={`{
  "total": 1,
  "campaigns": [
    {
      "analysis_id": "abc-123-def",
      "campaign_name": "SAT Marzo",
      "status": "completed",
      "total_rows": 5000,
      "summary": {
        "total": 5000, "celular": 3200,
        "fijo": 1500, "invalido": 300
      },
      "whatsapp_results": {
        "status": "completed",
        "tiene_whatsapp": 2100,
        "no_tiene_whatsapp": 1100,
        "errores": 0
      }
    }
  ]
}`} />
        </Section>

        <Section title="GET /external/campaigns/{id}/results - Resultados de campana" icon={Globe}>
          <p className="text-zinc-300 text-sm mb-3">Obtiene los resultados de una campana con paginacion y filtros.</p>
          <h4 className="text-sm font-semibold text-zinc-300 mt-4 mb-2">Parametros de query</h4>
          <div className="space-y-1 text-sm mb-3">
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><code className="text-violet-400 w-36">page</code><span className="text-zinc-300">int (default: 1) - Pagina</span></div>
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><code className="text-violet-400 w-36">page_size</code><span className="text-zinc-300">int (default: 100, max: 500) - Resultados por pagina</span></div>
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><code className="text-violet-400 w-36">filter_by</code><span className="text-zinc-300">string - all, celular, fijo, invalido, whatsapp_si, whatsapp_no</span></div>
          </div>
          <CodeBlock title="Request" code={`curl -X GET "${BASE_URL}/external/campaigns/abc-123/results?page=1&page_size=100&filter_by=celular" \\
  -H "X-API-Key: tu_api_key"`} />
          <CodeBlock title="Response" code={`{
  "campaign": { "analysis_id": "abc-123", "status": "completed", ... },
  "results": [
    {
      "original_number": "3816668820",
      "numero_normalizado": "0381 6668820",
      "tipo": "CELULAR",
      "tiene_whatsapp": "SI",
      "provincia": "Tucuman",
      "is_valid": true
    }
  ],
  "total": 3200,
  "page": 1,
  "page_size": 100,
  "total_pages": 32
}`} />
        </Section>

        <Section title="Codigos de error" icon={AlertTriangle}>
          <div className="space-y-1 text-sm">
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><Badge className="bg-rose-500/10 text-rose-400 border-rose-500/20 w-16 justify-center">401</Badge><span className="text-zinc-300">API Key invalida o faltante</span></div>
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 w-16 justify-center">400</Badge><span className="text-zinc-300">Parametros invalidos (phone requerido, max 500 en bulk)</span></div>
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 w-16 justify-center">429</Badge><span className="text-zinc-300">Limite mensual alcanzado</span></div>
            <div className="flex gap-3 p-2 bg-zinc-900 rounded"><Badge className="bg-zinc-700 text-zinc-300 w-16 justify-center">500</Badge><span className="text-zinc-300">Error interno del servidor</span></div>
          </div>
        </Section>

        <Section title="Ejemplos de integracion" icon={Code}>
          <h4 className="text-sm font-semibold text-zinc-300 mb-2">Python</h4>
          <CodeBlock title="python" lang="python" code={`import requests

API_KEY = "tu_api_key"
BASE = "${BASE_URL}"

# Individual
resp = requests.post(f"{BASE}/external/verify/phone",
    headers={"X-API-Key": API_KEY},
    json={"phone": "3816668820", "check_whatsapp": True})
print(resp.json())

# Masivo
resp = requests.post(f"{BASE}/external/verify/bulk",
    headers={"X-API-Key": API_KEY},
    json={"phones": ["3816668820", "1155551234"]})
print(resp.json()["summary"])`} />

          <h4 className="text-sm font-semibold text-zinc-300 mt-4 mb-2">JavaScript / Node.js</h4>
          <CodeBlock title="javascript" lang="javascript" code={`const API_KEY = "tu_api_key";
const BASE = "${BASE_URL}";

// Individual
const res = await fetch(\`\${BASE}/external/verify/phone\`, {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-API-Key": API_KEY },
  body: JSON.stringify({ phone: "3816668820", check_whatsapp: true })
});
console.log(await res.json());

// Masivo
const bulk = await fetch(\`\${BASE}/external/verify/bulk\`, {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-API-Key": API_KEY },
  body: JSON.stringify({ phones: ["3816668820", "1155551234"] })
});
console.log((await bulk.json()).summary);`} />
        </Section>
      </div>
    </div>
  );
}
